import {type SessionAccount} from '#/state/session/types'

/**
 * Base URL of the Cooey chat backend (delta), which brokers atproto sessions.
 * Overridable at build via EXPO_PUBLIC_COOEY_API.
 */
const COOEY_API =
  process.env.EXPO_PUBLIC_COOEY_API || 'https://chat.cooey.club/api'

/**
 * Full-page URL that starts the "Sign in with Cooey" flow: delta runs the
 * Discord/Authentik OIDC login and redirects back to `x.cooey.club/#h=<token>`
 * with a handoff token this module redeems. Most users never learn their
 * Authentik-managed atproto password, so this SSO path is the default sign-in.
 */
export const COOEY_LOGIN_URL = `${COOEY_API}/auth/oidc/login?flow=atproto`

/** Kick off the Cooey SSO login (web: top-level navigation to the IdP). */
export function startCooeyLogin() {
  if (typeof window !== 'undefined') {
    window.location.href = COOEY_LOGIN_URL
  }
}

type BrokeredSession = {
  accessJwt: string
  refreshJwt: string
  did: string
  handle: string
  pds: string
}

/**
 * One-click login from Cooey chat. Chat opens `x.cooey.club/#h=<token>` with a
 * short-lived, single-use handoff token that delta minted for the signed-in
 * chat user; we exchange it (`POST /auth/atproto/redeem`) for that user's own
 * atproto session and hand back a `SessionAccount` the app can `resumeSession`.
 *
 * Web only, and best-effort: returns undefined when there is no token or the
 * exchange fails, so the app falls back to its normal boot. The token is
 * stripped from the URL before the network call so it never lingers in history.
 */
export async function consumeCooeyHandoff(): Promise<
  SessionAccount | undefined
> {
  if (typeof window === 'undefined') return undefined

  const match = (window.location.hash || '').match(/[#&]h=([^&]+)/)
  if (!match) return undefined
  const token = decodeURIComponent(match[1])

  // Strip the token from the URL immediately, before we even use it.
  try {
    const url = new URL(window.location.href)
    url.hash = ''
    window.history.replaceState(null, '', url.toString())
  } catch {}

  try {
    const res = await fetch(`${COOEY_API}/auth/atproto/redeem`, {
      method: 'POST',
      headers: {'content-type': 'application/json'},
      body: JSON.stringify({token}),
    })
    if (!res.ok) return undefined
    const s = (await res.json()) as BrokeredSession

    /*
     * Only service/did/handle are required by the account schema; resumeSession
     * validates the JWTs against the PDS and refreshes in the rest (email etc.).
     */
    return {
      service: s.pds,
      did: s.did,
      handle: s.handle,
      accessJwt: s.accessJwt,
      refreshJwt: s.refreshJwt,
      active: true,
      pdsUrl: s.pds,
      isSelfHosted: true,
    }
  } catch {
    return undefined
  }
}
